import { z } from 'zod';
/**
 * Provider diagnostics and the provider keys behind them.
 *
 * The diagnostics never carry a key value, not even a fragment: they say where each
 * key comes from and nothing more. The value is read one key at a time through its
 * own resource, only when the operator asks to see it. Absence of a key is a state,
 * not an error - the Studio runs its whole product on the deterministic Mock path
 * with every key empty.
 *
 * The Tracker address and token are kept the same way and under the same paths.
 */
export const PROVIDERS_PATH = '/api/v1/providers';
export const PROVIDER_KEYS_PATH = '/api/v1/provider-keys';
/** The subscription image source chosen on the Providers page. PUT answers with the diagnostics. */
export const IMAGE_SOURCE_PATH = '/api/v1/image-source';
export function providerKeyPath(name) {
    return `${PROVIDER_KEYS_PATH}/${encodeURIComponent(name)}`;
}
/**
 * Where a provider key comes from. A key saved in the Studio wins over the deployment
 * environment; `unreadable` is a saved key the current SESSION_SECRET cannot decrypt.
 */
export const ProviderKeySourceSchema = z.enum(['studio', 'environment', 'unreadable', 'missing']);
export const ProviderKeyStatusSchema = z.object({
    /** The environment variable name the key is also read from. */
    name: z.string(),
    source: ProviderKeySourceSchema,
});
/** Keys are opaque strings; the bounds only keep an accidental paste out. */
export const SaveProviderKeyRequestSchema = z.object({
    value: z.string().trim().min(1).max(4096),
});
/** `null` returns app icons and feature graphics to the ordinary provider policy. */
export const ChooseImageSourceRequestSchema = z.object({
    providerId: z.string().min(1).max(64).nullable(),
});
export const ProviderKeyResourceSchema = z.object({
    name: z.string(),
    source: z.enum(['studio', 'environment']),
    value: z.string(),
});
/**
 * The last six belong to a subscription image source: a CLI signed in to an account
 * rather than a key. `ready` there means the client and its sign-in are in place, not
 * that the next image is guaranteed.
 */
export const ProviderStateSchema = z.enum([
    'ready',
    'missingKey',
    'unavailable',
    'disabled',
    'clientMissing',
    'incompatible',
    'needsLogin',
    'apiKeySignIn',
    'limitReached',
]);
export const PROVIDER_STATE_LABELS = {
    ready: 'готов',
    missingKey: 'нет ключа',
    unavailable: 'недоступен',
    disabled: 'не выбран',
    clientMissing: 'клиент не установлен',
    incompatible: 'несовместимый клиент',
    needsLogin: 'нужен вход',
    apiKeySignIn: 'вход не по подписке',
    limitReached: 'лимит исчерпан',
};
export const ProviderOperationSummarySchema = z.object({
    operation: z.string(),
    polling: z.boolean(),
    cancellation: z.boolean(),
    idempotency: z.boolean(),
    costReported: z.boolean(),
    /** A route with no price cannot be pinned, so an unpriced operation is unusable. */
    priced: z.boolean(),
    aspectRatios: z.array(z.string()),
    durationsSeconds: z.array(z.number()),
    maxReferences: z.number(),
});
export const ProviderModelSummarySchema = z.object({
    modelId: z.string(),
    operations: z.array(ProviderOperationSummarySchema),
});
export const ProviderUnsupportedSchema = z.object({
    /**
     * What the provider documents, named in the Studio's own vocabulary - an operation
     * such as `imageEdit`, or a way of using an operation such as
     * `imageGenerateReferences`.
     */
    capability: z.string(),
    /** Where it was read. Never a key, never a value. */
    source: z.string(),
});
export const ProviderDiagnosticSchema = z.object({
    providerId: z.string(),
    label: z.string(),
    state: ProviderStateSchema,
    /** Every key this provider needs and where each comes from. Never values. */
    keys: z.array(ProviderKeyStatusSchema),
    operations: z.array(z.string()),
    models: z.array(ProviderModelSummarySchema),
    note: z.string(),
    /**
     * What the provider documents and this Studio's adapter does not hold.
     *
     * A capability the provider offers and the Studio does not support is a fact about
     * the Studio, and it is said out loud rather than quietly missing.
     */
    unsupported: z.array(ProviderUnsupportedSchema),
    /** `subscription`: paid for by a signed-in account; its cost is not reported. */
    accounting: z.enum(['usd', 'subscription']),
});
export const ProviderDiagnosticsResponseSchema = z.object({
    providers: z.array(ProviderDiagnosticSchema),
    /**
     * Where the Tracker address and token come from: one entry per `TRACKER_KEY_NAMES`,
     * in that order. Never values.
     */
    tracker: z.array(ProviderKeyStatusSchema),
    /**
     * The subscription image source chosen on this page, by provider id, or null for the
     * ordinary provider policy.
     */
    imageSource: z.string().nullable(),
});
//# sourceMappingURL=providers.js.map